<script setup lang="ts">
import { useErrorStore } from '@/stores/errors'

const errorStore = useErrorStore()
</script>

<template>
  <TransitionGroup name="error-slide" tag="div" class="error-stack">
    <div
      v-for="error in errorStore.errors"
      :key="error.id"
      class="error-item"
      :class="`error-item--${error.kind}`"
      role="alert"
      aria-live="assertive"
    >
      <i
        class="pi error-icon"
        :class="error.kind === 'dev' ? 'pi-server' : 'pi-exclamation-circle'"
        aria-hidden="true"
      />
      <div class="error-body">
        <div v-if="error.kind === 'dev'" class="error-badge">Server error</div>
        <span class="error-message">{{ error.message }}</span>
        <details v-if="error.kind === 'dev' && error.technical" class="error-details">
          <summary class="error-details-toggle">Technical details</summary>
          <code class="error-technical">{{ error.technical }}</code>
        </details>
      </div>
      <button
        type="button"
        class="error-dismiss"
        aria-label="Dismiss error"
        @click="errorStore.dismiss(error.id)"
      >
        <i class="pi pi-times" aria-hidden="true" />
      </button>
    </div>
  </TransitionGroup>
</template>

<style scoped>
.error-stack {
  position: fixed;
  top: var(--space-5);
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: min(640px, calc(100vw - 2rem));
  pointer-events: none;
}

.error-item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-4) var(--space-5);
  pointer-events: auto;

  background: var(--color-danger-surface);
  border: 1px solid var(--color-danger-border);
  border-left: 4px solid var(--color-danger-accent);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
}

.error-icon {
  flex-shrink: 0;
  font-size: 1.25rem;
  color: var(--color-danger-accent);
  margin-top: 0.05rem;
}

.error-body {
  flex: 1;
  min-width: 0;
}

.error-badge {
  display: inline-block;
  margin-bottom: var(--space-1);
  padding: 0.1rem var(--space-2);
  font-size: var(--font-size-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-danger-text);
  background: var(--color-danger-border);
  border-radius: var(--radius-sm);
}

.error-message {
  display: block;
  font-size: var(--font-size-md);
  font-weight: 500;
  color: var(--color-danger-text);
  line-height: 1.5;
}

.error-details {
  margin-top: var(--space-2);
}

.error-details-toggle {
  font-size: var(--font-size-sm);
  color: var(--color-danger-accent);
  cursor: pointer;
  user-select: none;
}

.error-technical {
  display: block;
  margin-top: var(--space-1);
  padding: var(--space-2) var(--space-3);
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: var(--font-size-xs);
  color: var(--color-danger-text);
  background: color-mix(in srgb, var(--color-danger-border) 40%, transparent);
  border-radius: var(--radius-sm);
  word-break: break-all;
  white-space: pre-wrap;
}

.error-dismiss {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--space-6);
  height: var(--space-6);
  padding: 0;
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  color: var(--color-danger-accent);
  opacity: 0.6;
  transition: opacity var(--duration-ui) var(--ease-default);
}
.error-dismiss:hover {
  opacity: 1;
}
.error-dismiss:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-danger-accent);
  outline-offset: var(--space-px);
}
.error-dismiss .pi {
  font-size: var(--font-size-xs);
}

.error-slide-enter-active,
.error-slide-leave-active {
  transition:
    opacity 200ms var(--ease-default),
    transform 200ms var(--ease-default);
}
.error-slide-enter-from {
  opacity: 0;
  transform: translateY(calc(-1 * var(--space-2)));
}
.error-slide-leave-to {
  opacity: 0;
  transform: translateY(calc(-1 * var(--space-1))) scale(0.97);
}
.error-slide-move {
  transition: transform 200ms var(--ease-default);
}

@media (prefers-reduced-motion: reduce) {
  .error-slide-enter-active,
  .error-slide-leave-active,
  .error-slide-move {
    transition: none;
  }
}
</style>
